import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { EventCategory } from '../../hooks/useEvents';
import { formatCurrency } from '../../lib/currencyUtils';
import { getTicketAllocationSettings } from '../../lib/ticketAllocationUtils';
import { formatRegistrationDeadline } from '../../utils/dateUtils';

interface RegistrationModalProps {
  showRegistrationModal: boolean;
  selectedCategory: EventCategory | null;
  selectedEvent: any;
  includeToolCar: boolean;
  setIncludeToolCar: (value: boolean) => void;
  toolCarCount: number;
  setToolCarCount: (value: number) => void;
  isRegistering: boolean;
  handleRegistration: () => Promise<void>;
  onClose: () => void;
  formatDateForDisplay: (date: string) => string;
}

const RegistrationModal: React.FC<RegistrationModalProps> = ({
  showRegistrationModal,
  selectedCategory,
  selectedEvent,
  includeToolCar,
  setIncludeToolCar,
  toolCarCount,
  setToolCarCount,
  isRegistering,
  handleRegistration,
  onClose,
  formatDateForDisplay
}) => {
  const [maxToolCars, setMaxToolCars] = useState(0);
  const [ticketsEnabled, setTicketsEnabled] = useState(false);
  
  useEffect(() => {
    if (selectedEvent?.id) {
      fetchTicketSettings();
    }
  }, [selectedEvent]);
  
  const fetchTicketSettings = async () => {
    try {
      const settings = await getTicketAllocationSettings(selectedEvent.id);
      setMaxToolCars(settings.tool_car_max_amount);
      setTicketsEnabled(settings.tickets_enabled);
      
      // Only set tool car count and includeToolCar if tickets are enabled
      if (settings.tickets_enabled) {
        setToolCarCount(settings.tool_car_max_amount);
        setIncludeToolCar(settings.tool_car_max_amount > 0);
      } else {
        // If tickets are disabled, ensure tool cars are disabled too
        setToolCarCount(0);
        setIncludeToolCar(false);
      }
    } catch (error) {
      console.error('Error fetching ticket settings:', error);
      // Don't set default values that would override database settings
      setMaxToolCars(0);
      setTicketsEnabled(false);
      setToolCarCount(0);
      setIncludeToolCar(false);
    }
  };

  if (!showRegistrationModal || !selectedCategory || !selectedEvent) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-white">Confirm Registration</h2>
          <button onClick={onClose}>
            <X className="h-6 w-6 text-gray-400 hover:text-white" />
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-2">{selectedCategory.name}</h3>
          <p className="text-gray-400 mb-4">
            You are registering for this category in {selectedEvent.name}
          </p>

          <div className="text-sm text-gray-400">
            <p><strong>Registration Deadline:</strong> {formatRegistrationDeadline(selectedEvent.event_date)}</p>
            <p><strong>Event Date:</strong> {formatDateForDisplay(selectedEvent.event_date)}</p>
          </div>
        </div>
        
        {/* Only show tool car section if tickets are enabled AND max tool cars > 0 */}
        {ticketsEnabled && maxToolCars > 0 && (
          <div className="mb-6 p-4 bg-zinc-800 rounded-lg">
            <div className="bg-zinc-700 p-3 rounded-md">
              <div className="flex justify-between items-center">
                <span className="text-white">Tool Cars Included:</span>
                <span className="text-white font-bold">{maxToolCars}</span>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              This event automatically includes {maxToolCars} tool car{maxToolCars !== 1 ? 's' : ''} as set by the event administrator.
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Tool cars are for transporting tools and equipment only and are not eligible for competition.
            </p>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isRegistering}
            className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-lg font-semibold disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleRegistration}
            disabled={isRegistering}
            className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-lg font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isRegistering ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Processing...
              </>
            ) : (
              'Confirm Registration'
            )}
          </button>
        </div>

        {/* Payment Amount at Bottom */}
        <div className="mt-4 pt-4 border-t border-zinc-700">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold text-white">Amount to Pay:</span>
            <span className="text-2xl font-bold text-primary-500">
              {formatCurrency(selectedCategory.registration_fee)}
            </span>
          </div>
          <p className="text-sm text-gray-400 mt-1">Payment status will be pending until processed</p>
        </div>
      </div>
    </div>
  );
};

export default RegistrationModal;